import { Grid } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar/Avatar';
import LinearProgress from '@material-ui/core/LinearProgress';
import moment from 'moment';
import React, { Fragment, useEffect } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Alert, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import H4 from 'retired/H4';
import NonprofitLink from 'retired/NonprofitLink';
import PageHeaderRedesign from 'retired/PageHeaderRedesign';
import DialogButton from 'retired/DialogButton';
import { selectDonationScheduleById } from 'entities/donationSchedule/selectors';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import User from 'entities/user/model';
import DonationSchedule from 'entities/donationSchedule/model';
import { DonatePageSection, DonatePageSections } from 'components/DonatePageSection';
import Donation from 'containers/VolunteerPage/Donation';
import { dark } from 'utils/color-styles';
import { AmountCurrencies, currenciesFormat, CurrencyCode } from 'utils/currencyFormat';
import { State } from 'reducers';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';
import { Privileges } from 'utils/privileges';
import { truthy } from 'utils/truthy';
import { displayName } from 'utils/formatFullName';
import { DonationScheduleStatus } from 'generated/graphql';

import * as Actions from './actions';
import epics from './epics';
import reducer from './reducer';
import { selectLoading, selectError } from './selectors';

const momentFormat = 'MMM DD, YYYY';

const styles = {
  headStyle: {
    fontSize: 15,
    fontWeight: 500,
  },
  textStyle: {
    fontSize: 15,
    fontWeight: 300,
  },
  linkStyle: {
    display: 'block',
    fontSize: 15,
    fontWeight: 300,
    color: dark,
    textDecoration: 'underline',
  },
};

const key = 'donationSchedulePage';

const UserLink = ({ user }: { user: User }): JSX.Element => {
  const { name, mainPicture, id } = user;

  return (
    <div
      style={{
        width: '100%',
        marginBottom: 20,
        marginTop: 20,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Avatar
        alt={name}
        src={mainPicture.replace('http://', 'https://')}
        style={{
          width: 40,
          height: 40,
          float: 'left',
          marginRight: 10,
        }}
      />
      <Link
        to={`/volunteers/${id}`}
        style={{
          ...styles.linkStyle,
          lineHeight: '40px',
        }}
      >
        {displayName(user)}
      </Link>
    </div>
  );
};

const getFrequencyLabel = (frequency: string) => {
  const frequencyOptions = {
    P1M: 'Monthly',
    P3M: 'Quarterly',
    P1Y: 'Annually',
    PPC: 'Every Paycheck',
    OneTime: 'One Time',
    Payroll: 'Payroll',
  };
  return frequencyOptions[frequency as keyof typeof frequencyOptions] || frequency;
};

const DonationSchedulePage = (): JSX.Element => {
  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });

  const dispatch = useDispatch();
  const history = useHistory();
  const { id: donationScheduleId } = useParams<{ id: string }>();

  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const organization = useSelector(selectOrganization);
  const user = useSelector(selectUser);
  const donationSchedule: DonationSchedule | undefined = useSelector((state: State) =>
    selectDonationScheduleById(state, donationScheduleId)
  );

  const description =
    donationSchedule?.description ||
    `Donation Schedule for ${donationSchedule?.nonprofits.map((n) => n.name).join(', ')}`;

  useEffect(() => {
    if (user && donationScheduleId) {
      dispatch(Actions.initAction(donationScheduleId));
    }
  }, [user, donationScheduleId]);

  useEffect(() => {
    if (!user?.hasPrivilege(Privileges.DONATION_SCHEDULE_VIEW)) {
      history.replace('/');
    }
  }, [user]);

  const onCancelDonationSchedule = () => {
    dispatch(Actions.cancelDonationScheduleAction(donationScheduleId));
  };

  if (error) {
    return (
      <Alert severity="error">
        There was an error fetching a donation
        <br />
        <br />
        {error?.message}
      </Alert>
    );
  }

  // Show loading only if we don't have the basic schedule yet
  // If we have the schedule but not donations, we'll show schedule details with donations loading
  if (!donationSchedule || !user) {
    return <LinearProgress />;
  }

  const loadingDonations = !donationSchedule.donations;

  return (
    <>
      <Helmet title={description} />
      <PageHeaderRedesign title={description} onArrowClick={() => history.goBack()} />
      <div
        style={{
          marginLeft: 20,
          marginRight: 20,
        }}
      >
        <Grid container spacing={10}>
          <Grid item md={12}>
            <div
              style={{
                flexGrow: 1,
                paddingTop: 0,
                marginBottom: 30,
                borderRadius: 12,
                border: '1px solid rgba(230, 230, 230, 1)',
                padding: 32,
                backgroundColor: 'white',
              }}
            >
              <div style={{ width: '100%' }}>
                <H4>Donation Schedule details</H4>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  {donationSchedule.user ? <UserLink user={donationSchedule.user?.toJS()} /> : null}
                  {user?.hasPrivilege(Privileges.DONATION_SCHEDULE_MANAGE) &&
                    donationSchedule.status === DonationScheduleStatus.Active && (
                      <DialogButton
                        title="Cancel donation schedule"
                        text="Are you sure you want to cancel this donation schedule?"
                        confirmLabel="Confirm cancel"
                        cancelLabel="Go back"
                        handleConfirm={() => onCancelDonationSchedule()}
                        customButton={(props: any) => <Button {...props} />}
                        buttonProps={{
                          variant: 'outlined',
                          color: 'error',
                          endIcon: <DeleteIcon />,
                        }}
                      >
                        Cancel Donation Schedule
                      </DialogButton>
                    )}
                </div>

                <DonatePageSections>
                  {[
                    donationSchedule.campaign?.id && organization && (
                      <DonatePageSection
                        key="campaign"
                        title="Campaign"
                        value={
                          donationSchedule.campaign?.company?.id === organization.id ? (
                            <Link
                              to={`/campaigns/${donationSchedule.campaign?.id}`}
                              style={styles.linkStyle}
                            >
                              {donationSchedule.campaign?.title}
                            </Link>
                          ) : (
                            <div style={styles.textStyle}>{donationSchedule.campaign?.title}</div>
                          )
                        }
                      />
                    ),
                    donationSchedule.nonprofits?.size > 0 && (
                      <DonatePageSection
                        key="nonprofit"
                        title="Nonprofit"
                        value={
                          <>
                            {donationSchedule.nonprofits?.map(
                              (nonprofit, i) =>
                                nonprofit && (
                                  <Fragment key={nonprofit.id}>
                                    <NonprofitLink nonprofit={nonprofit} />
                                    {i + 1 < donationSchedule.nonprofits.size ? ', ' : ''}
                                  </Fragment>
                                )
                            )}
                          </>
                        }
                      />
                    ),
                    <DonatePageSection
                      key="amount"
                      title="Donation Amount"
                      value={
                        donationSchedule.amount
                          ? currenciesFormat(
                              {
                                [donationSchedule.currencyCode]: donationSchedule.amount,
                              } as AmountCurrencies,
                              donationSchedule.currencyCode as CurrencyCode
                            )
                          : '-'
                      }
                    />,
                    <DonatePageSection
                      key="provider"
                      title="Donation processor"
                      value={donationSchedule.provider}
                    />,
                    <DonatePageSection
                      key="status"
                      title="Status"
                      value={donationSchedule.status}
                    />,
                    <DonatePageSection
                      key="privacy"
                      title="Privacy"
                      value={donationSchedule.privacy}
                    />,
                    <DonatePageSection
                      key="paymentMethod"
                      title="Payment method"
                      value={donationSchedule.paymentMethod?.type}
                    />,
                    <DonatePageSection
                      key="createdAt"
                      title="Created At"
                      value={
                        donationSchedule.createdAt
                          ? moment(donationSchedule.createdAt).format(momentFormat)
                          : '-'
                      }
                    />,
                    <DonatePageSection
                      key="startingAt"
                      title="Starting At"
                      value={
                        donationSchedule.startingAt
                          ? moment(donationSchedule.startingAt).format(momentFormat)
                          : '-'
                      }
                    />,
                    donationSchedule.endingAt && (
                      <DonatePageSection
                        key="endingAt"
                        title={`${donationSchedule.endingAt < new Date() ? 'Ended' : 'Ending'} At`}
                        value={moment(donationSchedule.endingAt).format(momentFormat)}
                      />
                    ),
                    <DonatePageSection
                      key="frequency"
                      title="Frequency"
                      value={getFrequencyLabel(donationSchedule?.frequency)}
                    />,
                    donationSchedule?.executionFailure?.size > 0 && (
                      <DonatePageSection
                        key="executionFailure"
                        title="Execution Failure"
                        value={donationSchedule?.executionFailure
                          .map(
                            (failure) =>
                              `[${failure?.code}]: ${failure?.message} - ${failure?.failedAt}`
                          )
                          .join(', ')}
                      />
                    ),
                  ].filter(truthy)}
                </DonatePageSections>
              </div>
            </div>

            <div
              style={{
                flexGrow: 1,
                paddingTop: 0,
                marginBottom: 30,
                borderRadius: 12,
                border: '1px solid rgba(230, 230, 230, 1)',
                padding: 32,
                backgroundColor: 'white',
              }}
            >
              <H4 style={{ marginBottom: 20 }}>Donations</H4>
              {loadingDonations ? (
                <div style={{ marginTop: 30, textAlign: 'center' }}>
                  <LinearProgress />
                </div>
              ) : donationSchedule.donations?.size > 0 ? (
                donationSchedule.donations
                  .toArray()
                  .map((donation) => <Donation key={donation.id} donation={donation} user={user} />)
              ) : (
                <div style={{ marginTop: 30 }}>No donations to show</div>
              )}
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default DonationSchedulePage;
